import { useCallback, useEffect, useState } from 'react'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import {
  BulkSheetFilter,
  FilterFooter,
  FilterInput,
} from '../../../../containers/BulkSheetView/components/filter/common'
import {
  SelectFilterArea,
  SelectFilterFormControl,
  CheckboxArea,
  FilterLabel,
} from '../../../../containers/BulkSheetView/components/filter/SelectFilter'
import { Checkbox, Typography } from '@mui/material'
import { ICON_SIZE } from '../../../../containers/BulkSheet/const'

export type SelectFilter = {
  values: string[]
  includeBlank: boolean
}

type Props = CustomFilterProps & {
  getValue: (option: any) => string
  getLabel: (option: any) => string
  valueGetter: (params: any) => any
  valueFormatter: (params: any) => string
  sortValues: (uiMeta: any, options: any, context: any) => any
  values?: any[]
}

const buildSelectFilterModel = (
  values: string[],
  includeBlank: boolean,
  allOptionValues: string[]
): SelectFilter | null => {
  const isAllSelected =
    allOptionValues.length > 0 &&
    values.length === allOptionValues.length &&
    includeBlank
  return isAllSelected ? null : { values, includeBlank }
}

export const FixedSelectFilter = ({
  model,
  onModelChange,
  getValue,
  getLabel,
  valueGetter,
  valueFormatter,
  sortValues,
  colDef,
  context,
  api,
}: Props) => {
  // UI-local state
  const [text, setText] = useState('')
  const [options, setOptions] = useState<any[]>([])

  // Derive state from model (single source of truth)
  const allOptionValues = options.map(getValue)
  const values = (model as SelectFilter | null)?.values ?? allOptionValues
  const includeBlank = (model as SelectFilter | null)?.includeBlank ?? true
  const isAllSelected =
    !model || (values.length === allOptionValues.length && includeBlank)
  const isAllDeselected = model !== null && values.length === 0 && !includeBlank

  const getOptions = useCallback(() => {
    if (colDef.filterParams?.values) {
      return colDef.filterParams.values
    }

    const initialOptions: any[] = []
    api.forEachNode(node => {
      initialOptions.push(valueGetter({ node, context }))
    })
    const newOptionValues: string[] = []
    const newOptions: any[] = []
    initialOptions
      .filter(option => !!option)
      .forEach(option => {
        if (Array.isArray(option)) {
          option.forEach(o => {
            if (!newOptionValues.includes(getValue(o))) {
              newOptions.push(o)
              newOptionValues.push(getValue(o))
            }
          })
        } else if (!newOptionValues.includes(getValue(option))) {
          newOptions.push(option)
          newOptionValues.push(getValue(option))
        }
      })
    if (sortValues) {
      return sortValues(colDef.cellRendererParams?.uiMeta, newOptions, context)
    } else {
      return newOptions
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api/context/valueGetter/getValue/sortValues/colDef は毎レンダーで新規参照になるため deps に入れない
  }, [])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.isAdded) return true
      if (!model) return true

      const selectValue = valueGetter({
        ...params,
        context,
      })
      if (
        (model as SelectFilter).includeBlank &&
        (!selectValue ||
          (Array.isArray(selectValue) && selectValue.length === 0))
      ) {
        return true
      }
      if (
        Array.isArray(selectValue) &&
        selectValue.some(v =>
          (model as SelectFilter).values.includes(getValue(v))
        )
      ) {
        return true
      } else if (
        (model as SelectFilter).values.includes(getValue(selectValue))
      ) {
        return true
      }
      return false
    },

    afterGuiAttached() {
      setOptions(getOptions())
    },

    getModelAsString() {
      if (!model) return ''
      let labelList: string[] = []
      includeBlank && labelList.push('(Blank)')
      labelList = labelList.concat(
        options
          .filter(option => values.includes(getValue(option)))
          .map(option => getLabel(option))
      )
      return `${labelList.join(',')}`
    },
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行
  useEffect(() => {
    setOptions(getOptions())
  }, [])

  // Helpers for model updates
  const updateValues = (newValues: string[]) => {
    onModelChange(
      buildSelectFilterModel(newValues, includeBlank, allOptionValues)
    )
  }

  const updateIncludeBlank = (newIncludeBlank: boolean) => {
    onModelChange(
      buildSelectFilterModel(values, newIncludeBlank, allOptionValues)
    )
  }

  return (
    <BulkSheetFilter>
      <FilterInput
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'Search...'}
        sx={{ height: 22 }}
      />
      <SelectFilterArea>
        <SelectFilterFormControl
          label="(Select All)"
          control={
            <Checkbox
              checked={isAllSelected}
              indeterminate={!isAllSelected && !isAllDeselected}
              onChange={e =>
                e.target.checked
                  ? onModelChange(null)
                  : onModelChange({ values: [], includeBlank: false })
              }
              disableRipple={true}
            />
          }
        />
        <CheckboxArea>
          {!text && (
            <SelectFilterFormControl
              label="(Blanks)"
              control={
                <Checkbox
                  checked={includeBlank}
                  onChange={e => updateIncludeBlank(e.target.checked)}
                  disableRipple={true}
                />
              }
            />
          )}
          {options
            .filter(
              option =>
                !text ||
                (valueFormatter
                  ? valueFormatter({
                      colDef,
                      value: getLabel(option),
                    }).includes(text)
                  : getLabel(option).includes(text))
            )
            .map(option => {
              const value = getValue(option)
              return (
                <SelectFilterFormControl
                  key={`select-filter-${value}`}
                  label={
                    <FilterLabel
                      sx={{
                        backgroundColor: option.backgroundColor ?? 'inherit',
                      }}
                    >
                      {option.iconUrl && (
                        <img
                          src={option.iconUrl}
                          style={{
                            height: `${ICON_SIZE}px`,
                            width: `${ICON_SIZE}px`,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      <Typography sx={{ padding: '0 3px' }}>
                        {valueFormatter
                          ? valueFormatter({
                              colDef,
                              value: getLabel(option),
                            })
                          : getLabel(option)}
                      </Typography>
                    </FilterLabel>
                  }
                  control={
                    <Checkbox
                      checked={values.includes(value)}
                      onChange={e => {
                        if (e.target.checked) {
                          updateValues([...values, value])
                        } else {
                          updateValues(values.filter(v => v !== value))
                        }
                      }}
                      disableRipple={true}
                    />
                  }
                />
              )
            })}
        </CheckboxArea>
      </SelectFilterArea>
      <FilterFooter loading={false} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
